import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// Ensure autoTable is properly attached (handles ESM/CJS interop issues)
const _autoTable = (autoTable as unknown as { default?: typeof autoTable }).default ?? autoTable;
import type { PaymentRecord, AccountingKPIs, AccountingFilters, PeriodFilter } from '@/hooks/useAccounting';

function formatCOP(amount: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}  ${time}`;
}

function getPeriodLabel(filters: AccountingFilters): string {
  const labels: Record<PeriodFilter, string> = {
    today: 'Hoy',
    week: 'Esta semana',
    month: 'Este mes',
    year: 'Este año',
    custom: `${filters.dateFrom} — ${filters.dateTo}`,
  };
  return labels[filters.period];
}

function getAppointmentStatusLabel(status: string): string {
  const map: Record<string, string> = {
    completed: 'Completado',
    no_show: 'No asistió',
    confirmed: 'Confirmada',
    pending: 'Pendiente',
    cancelled: 'Cancelada',
  };
  return map[status] ?? status;
}

function getTypeLabel(type: string): string {
  return type === 'advance' ? 'Anticipo' : 'Restante';
}

function getMethodLabel(method: string, amount: number): string {
  // Mock payments: amount=$0 via wompi = simulated, not a real charge
  if (method === 'wompi' && amount === 0) return 'Simulado ($0)';
  const map: Record<string, string> = {
    wompi: 'Wompi',
    cash: 'Efectivo',
    transfer: 'Transferencia',
  };
  return map[method] ?? method;
}

export function exportAccountingPDF(
  payments: PaymentRecord[],
  kpis: AccountingKPIs,
  filters: AccountingFilters,
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const pageW = doc.internal.pageSize.getWidth();
  const today = new Date();
  const generatedDate = today.toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  // ─── HEADER ────────────────────────────────────────────────────────────────
  // Top accent bar
  doc.setFillColor(30, 30, 30);
  doc.rect(0, 0, pageW, 28, 'F');

  // Studio name (left)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('BARBER STUDIO', 14, 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(160, 160, 160);
  doc.text('Panel de Administración', 14, 18);

  // Report title (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text('REPORTE FINANCIERO', pageW - 14, 12, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(160, 160, 160);
  doc.text(`Período: ${getPeriodLabel(filters)}`, pageW - 14, 18, { align: 'right' });
  doc.text(`Generado: ${generatedDate}`, pageW - 14, 23, { align: 'right' });

  // ─── KPI SUMMARY ───────────────────────────────────────────────────────────
  let y = 36;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('RESUMEN DE INGRESOS', 14, y);

  y += 5;

  // KPI boxes
  const kpiData = [
    { label: 'Ingresos Totales', value: formatCOP(kpis.totalRevenue), accent: true },
    { label: 'Ingresos por Anticipos', value: formatCOP(kpis.advanceRevenue), accent: false },
    { label: 'Ingresos Restantes', value: formatCOP(kpis.remainingRevenue), accent: false },
    { label: 'Total Transacciones', value: `${kpis.transactionCount} pagos`, accent: false },
  ];

  const boxW = (pageW - 28 - 9) / 4;
  const boxH = 18;

  kpiData.forEach((kpi, i) => {
    const x = 14 + i * (boxW + 3);

    // Box background
    if (kpi.accent) {
      doc.setFillColor(240, 255, 248);
      doc.setDrawColor(52, 211, 153);
    } else {
      doc.setFillColor(248, 248, 248);
      doc.setDrawColor(220, 220, 220);
    }
    doc.roundedRect(x, y, boxW, boxH, 2, 2, 'FD');

    // Value
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(kpi.accent ? 20 : 30, kpi.accent ? 120 : 30, kpi.accent ? 80 : 30);
    doc.text(kpi.value, x + boxW / 2, y + 8, { align: 'center' });

    // Label
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(kpi.label, x + boxW / 2, y + 14, { align: 'center' });
  });

  y += boxH + 10;

  // ─── DIVIDER ───────────────────────────────────────────────────────────────
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(14, y - 4, pageW - 14, y - 4);

  // ─── TABLE TITLE ───────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('LIBRO DE TRANSACCIONES', 14, y);
  y += 4;

  // ─── TABLE ─────────────────────────────────────────────────────────────────
  const tableRows = payments.map((p) => [
    formatDateTime(p.created_at),
    p.appointment?.client_name ?? '—',
    p.appointment ? getAppointmentStatusLabel(p.appointment.status) : '—',
    getTypeLabel(p.type),
    getMethodLabel(p.method, Number(p.amount)),
    formatCOP(Number(p.amount)),
    p.appointment ? `${p.appointment.appointment_date}  ${p.appointment.start_time?.slice(0, 5) ?? ''}` : '—',
    p.reference ? p.reference.slice(0, 20) : '—',
  ]);

  // Totals row
  const totalAmount = payments.reduce((sum, p) => sum + Number(p.amount), 0);
  tableRows.push([
    '',
    '',
    '',
    '',
    'TOTAL',
    formatCOP(totalAmount),
    '',
    '',
  ]);

  _autoTable(doc, {
    startY: y,
    head: [['Fecha y hora', 'Cliente', 'Estado cita', 'Tipo', 'Método', 'Monto', 'Cita', 'Referencia']],
    body: tableRows,
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
      textColor: [40, 40, 40],
      lineColor: [230, 230, 230],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [30, 30, 30],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'left',
    },
    columnStyles: {
      5: { halign: 'right', fontStyle: 'bold' },
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    // Style the totals row (last row)
    didParseCell: (data) => {
      if (data.row.index === tableRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [240, 255, 248];
        data.cell.styles.textColor = [20, 120, 80];
      }
    },
    margin: { left: 14, right: 14 },
  });

  // ─── FOOTER ────────────────────────────────────────────────────────────────
  const pageCount = (doc.internal as { getNumberOfPages: () => number }).getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageH = doc.internal.pageSize.getHeight();

    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(14, pageH - 12, pageW - 14, pageH - 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text('Generado automáticamente por el sistema — Barber Studio', 14, pageH - 7);
    doc.text(`Página ${i} de ${pageCount}`, pageW - 14, pageH - 7, { align: 'right' });
  }

  // ─── SAVE ──────────────────────────────────────────────────────────────────
  const dateStr = today.toISOString().split('T')[0];
  doc.save(`reporte-${dateStr}.pdf`);
}
